import "./CachePage.scss";

import classnames from "classnames";
import React, { useCallback, useEffect, useState } from "react";

import { LoadingOverlay } from "../../../../components/LoadingOverlay/LoadingOverlay";
import type { IOrganization } from "../../../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../../../hooks/reactQueryHooks/useApplications";
import type { ICacheItemsRequest } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { useCacheItems } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import type { Direction } from "../../../../hooks/useSortableTable";
import { Badges } from "../Badges";
import CacheTable from "../CacheTable/CacheTable";
import { NoCacheView } from "../NoCacheView";

interface IProps {
  currentOrganization: IOrganization | undefined;
  applications: Array<IApplication>;
  setTotalItems: (total: number) => void;
  setShowExportComponent: (show: boolean) => void;
}

const CachePage: React.FC<IProps> = ({
  currentOrganization,
  applications,
  setTotalItems,
  setShowExportComponent,
}) => {
  const { queryValue: appQuery } = useQueryParams({
    queryStr: "projId",
  });
  const { queryValue: pageQuery, handleChange } = useQueryParams({
    queryStr: "p",
  });
  const { queryValue: searchQuery } = useQueryParams({
    queryStr: "s",
  });

  const [orderBy, setOrderBy] = useState<string | undefined>();
  const [direction, setDirection] = useState<boolean | undefined>();

  const request: ICacheItemsRequest = {
    PageIndex: pageQuery ? Number(pageQuery) : 1,
    Search: searchQuery ? searchQuery : undefined,
    ApplicationId: appQuery ? appQuery : undefined,
    orderBy,
    desc: direction,
  };

  const { data, isLoading, isFetching, error } = useCacheItems(
    currentOrganization?.id || "",
    request,
  );

  const handleSort = useCallback(
    (orderBy: string, direction: Direction) => {
      if (direction !== null) {
        setOrderBy(orderBy);
        setDirection(direction === "desc");
        return;
      }
      setDirection(undefined);
      setOrderBy(undefined);
    },
    [setOrderBy, setDirection],
  );

  useEffect(() => {
    setTotalItems(0);
  }, [currentOrganization, setTotalItems]);

  const { items, filteredCount, totalCount: total, cacheHitRatio } = data || {};

  useEffect(() => {
    setTotalItems(filteredCount || 0);
    setShowExportComponent(!!filteredCount || !!searchQuery || !!appQuery);
  }, [
    filteredCount,
    setTotalItems,
    searchQuery,
    appQuery,
    pageQuery,
    setShowExportComponent,
  ]);

  const showNoCacheView =
    items &&
    items.length === 0 &&
    !isLoading &&
    !isFetching &&
    !appQuery &&
    !searchQuery;

  const showTable = !error && !!items && !showNoCacheView;

  const wrapperClass = classnames({
    "app-container-inner-wrapper": true,
    "justify-content-center": !showTable,
    "justify-content-start": showTable,
    "flex-column": showTable,
    "d-flex": true,
    "h-100": true,
  });

  const goPrevPage = () => {
    const page = pageQuery ? Number(pageQuery) : 1;
    if (page - 1 <= 1) {
      handleChange("", true);
      return;
    }
    if (page - 1) {
      handleChange((page - 1).toString(), true);
    }
  };

  return (
    <>
      <Badges
        cachedItemsCount={total || 0}
        cacheHitRatio={cacheHitRatio || 0}
        isLoading={isLoading || isFetching}
      />
      <div className="card flex-shrink-1 flex-grow-1">
        <div className={wrapperClass}>
          {(isFetching || isLoading) && <LoadingOverlay />}
          {showTable ? (
            <CacheTable
              applications={applications}
              items={items || []}
              totalItems={filteredCount}
              isLoading={isLoading || isFetching}
              goPrevPage={goPrevPage}
              onSort={handleSort}
            />
          ) : (
            showNoCacheView && <NoCacheView />
          )}
        </div>
      </div>
    </>
  );
};

export { CachePage };
